import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
} from '@mui/material';
import { Save, Settings, DirectionsCar, Backup, ChevronRight } from '@mui/icons-material';
import apiService from '../services/apiService';

const SettingsPage = () => {
  const [activeSection, setActiveSection] = useState('general');
  const [settings, setSettings] = useState({
    platformName: 'Pushpa Taxi',
    supportEmail: 'support@pushpa.com',
    supportPhone: '+919876543210',
    commissionRate: 10,
    searchRadius: 5,
    gstPercent: 18,
    rideRequestTimer: 30,
    rebroadcastTimer: 20,
    searchDriverTimeout: 120,
  });
  const [backupSettings, setBackupSettings] = useState({
    email: '',
    googleDriveLink: '',
    frequency: 'daily',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const sections = [
    { id: 'general', label: 'General Settings', icon: <Settings /> },
    { id: 'ride', label: 'Ride Configuration', icon: <DirectionsCar /> },
    { id: 'backup', label: 'Database Backup', icon: <Backup /> },
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await apiService.getSettings();
      if (response.success) {
        setSettings(response.data);
      }
    } catch (err) {
      console.error('Settings error:', err);
    }
  };

  const handleChange = (e) => {
    setSettings({ ...settings, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setSuccess('');
      setError('');

      const response = await apiService.updateSettings(settings);

      if (response.success) {
        setSuccess('Settings updated successfully');
      } else {
        setError(response.message || 'Failed to update settings');
      }
    } catch (err) {
      setError('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const handleBackupChange = (e) => {
    setBackupSettings({ ...backupSettings, [e.target.name]: e.target.value });
  };

  const handleSaveBackup = async () => {
    try {
      setLoading(true);
      setSuccess('');
      setError('');

      // Validate at least one field is filled
      if (!backupSettings.email && !backupSettings.googleDriveLink) {
        setError('Please provide at least an email or Google Drive link');
        setLoading(false);
        return;
      }

      // Call real backup API
      const response = await apiService.updateBackupSettings(backupSettings);
      
      if (response.success) {
        setSuccess(response.message || 'Backup settings saved and backup generated successfully');
      } else {
        setError(response.message || 'Failed to save backup settings');
      }
      setLoading(false);
    } catch (err) {
      setError(err.message || 'Failed to save backup settings');
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Platform Settings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure platform-wide settings
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left Sidebar - Section Menu */}
        <Grid item xs={12} md={3}>
          <Paper elevation={2}>
            <List sx={{ py: 1 }}>
              {sections.map((section) => (
                <ListItem key={section.id} disablePadding>
                  <ListItemButton
                    selected={activeSection === section.id}
                    onClick={() => setActiveSection(section.id)}
                    sx={{
                      borderRadius: 1,
                      mx: 1,
                      mb: 0.5,
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                        },
                        '& .MuiListItemIcon-root': {
                          color: 'white',
                        },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: activeSection === section.id ? 'white' : 'text.secondary',
                        minWidth: 40,
                      }}
                    >
                      {section.icon}
                    </ListItemIcon>
                    <ListItemText primary={section.label} />
                    <ChevronRight />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Right Content - Active Section */}
        <Grid item xs={12} md={9}>
          {/* General Settings Section */}
          {activeSection === 'general' && (
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Settings color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    General Settings
                  </Typography>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Platform Name"
                      name="platformName"
                      value={settings.platformName}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Support Email"
                      name="supportEmail"
                      type="email"
                      value={settings.supportEmail}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Support Phone"
                      name="supportPhone"
                      value={settings.supportPhone}
                      onChange={handleChange}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<Save />}
                      onClick={handleSave}
                      disabled={loading}
                    >
                      Save Settings
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Ride Configuration Section */}
          {activeSection === 'ride' && (
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <DirectionsCar color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    Ride Configuration
                  </Typography>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Search Radius (km)"
                      name="searchRadius"
                      type="number"
                      value={settings.searchRadius}
                      onChange={handleChange}
                      helperText="Radius to search for nearby drivers (e.g., 3km, 5km)"
                      inputProps={{ min: 1, max: 50 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Ride Request Timer (seconds)"
                      name="rideRequestTimer"
                      type="number"
                      value={settings.rideRequestTimer}
                      onChange={handleChange}
                      helperText="How long ONE driver sees a single ride request before it auto-disappears (e.g., 10, 20, 30)"
                      inputProps={{ min: 5, max: 120 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Rebroadcast Timer (seconds)"
                      name="rebroadcastTimer"
                      type="number"
                      value={settings.rebroadcastTimer}
                      onChange={handleChange}
                      helperText="How often the backend re-fans an unaccepted ride request to fresh nearby drivers (e.g., 15, 20, 30)"
                      inputProps={{ min: 5, max: 120 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Search Driver Timeout (seconds)"
                      name="searchDriverTimeout"
                      type="number"
                      value={settings.searchDriverTimeout}
                      onChange={handleChange}
                      helperText="Total time the backend keeps searching before giving up and showing 'No driver found' (default: 120 = 2 mins)"
                      inputProps={{ min: 30, max: 600 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Commission Rate (%)"
                      name="commissionRate"
                      type="number"
                      value={settings.commissionRate}
                      onChange={handleChange}
                      disabled
                      helperText="Platform commission from each ride (Coming soon)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="GST Percentage (%)"
                      name="gstPercent"
                      type="number"
                      value={settings.gstPercent}
                      onChange={handleChange}
                      disabled
                      helperText="GST applied on rides (Coming soon)"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<Save />}
                      onClick={handleSave}
                      disabled={loading}
                    >
                      Save Settings
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Database Backup Section */}
          {activeSection === 'backup' && (
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Backup color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    Database Backup
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Configure automatic database backups. Provide at least one destination (email or Google Drive).
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Backup Email"
                      name="email"
                      type="email"
                      value={backupSettings.email}
                      onChange={handleBackupChange}
                      helperText="Email address to receive database backups"
                      placeholder="admin@example.com"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Google Drive Link"
                      name="googleDriveLink"
                      value={backupSettings.googleDriveLink}
                      onChange={handleBackupChange}
                      helperText="Google Drive folder link for backups"
                      placeholder="https://drive.google.com/..."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Backup Frequency</InputLabel>
                      <Select
                        name="frequency"
                        value={backupSettings.frequency}
                        onChange={handleBackupChange}
                        label="Backup Frequency"
                      >
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="weekly">Weekly</MenuItem>
                        <MenuItem value="monthly">Monthly</MenuItem>
                      </Select>
                      <FormHelperText>How often to automatically backup the database</FormHelperText>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Backups will be created automatically based on the selected frequency. The SQL database file will be exported and sent to the configured destinations.
                    </Alert>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<Save />}
                      onClick={handleSaveBackup}
                      disabled={loading}
                    >
                      Save Settings
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default SettingsPage;
